import { useEffect, useCallback, useMemo } from 'react'
import { useDispatch, TypedUseSelectorHook, useSelector as useSelectorReactRedux } from 'react-redux'

import { FirstArg, FirstReturn, TypeOfOperation } from './typeHelpers'
import { useApiDispatch, ConnectedApiOperation } from './useApiDispatch'
import { resetApiOperationState } from './actions'
import { CompiledApiOperations } from './operationHelper'
import { State } from './reducerHelper'

type CustomApiSelectorFn<TReduxState, TApiState> = <K extends keyof TApiState>(state: TReduxState, requestName: K) => State<any>
export const createUseApiSelector = <TReduxState, TApiState>(apiSelector: CustomApiSelectorFn<TReduxState, TApiState>) => <T extends keyof TApiState>(requestName: T): TApiState[T] => {
  const useSelector: TypedUseSelectorHook<any> = useSelectorReactRedux
  const requestState = useSelector(state => apiSelector(state, requestName as any))
  return requestState as any
}

export type ClearRequestStateFn = () => void
export type UseApiParams = {
  clearOnUnmount?: boolean
}
export type UseApiResult<TApiState, TCompiledOperations extends CompiledApiOperations<any>, K extends keyof TApiState> = [ConnectedApiOperation<FirstArg<TCompiledOperations[K]>, TypeOfOperation<FirstReturn<TCompiledOperations[K]>>>, NonNullable<TApiState[K]>, ClearRequestStateFn]
export type UseApi<TApiState, TCompiledOperations extends CompiledApiOperations<any>> = <T extends keyof TApiState>(requestName: T, useApiParams?: UseApiParams) => UseApiResult<TApiState, TCompiledOperations, T>

export const createUseApi = <TCompiledOperations extends CompiledApiOperations<any>, TApiState>(compiledApiOperations: TCompiledOperations, useApiSelector: FirstReturn<typeof createUseApiSelector>): UseApi<TApiState, TCompiledOperations> => {
  return <T extends keyof TApiState>(requestName: T, options: UseApiParams = {}): UseApiResult<TApiState, TCompiledOperations, T> => {
    const dispatch = useDispatch()
    const requestState = useApiSelector(requestName as never)
    const { connectedRequestOperation } = useMemo(() => useApiDispatch({ connectedRequestOperation: compiledApiOperations[requestName] }, dispatch), [requestName, dispatch])
    const clearRequestState = useCallback(() => {
      dispatch(resetApiOperationState(requestName as any))
    }, [dispatch, requestName])

    useEffect(() => {
      return () => {
        if (options.clearOnUnmount) {
          clearRequestState()
        }
      }
    }, [options.clearOnUnmount, clearRequestState])

    return [connectedRequestOperation, requestState, clearRequestState as any] as any
  }
}
